import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Nav, Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Flex from '../../../components/common/Flex';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { PENDING_TAB_FETCH_KEY, useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import AcceptingOrderPendings from './AcceptingOrderPendings';
import AccountsReceivableOrderPendings from './AccountsReceivableOrderPendings';
import ApplyingOrderPendings from './ApplyingOrderPendings';
import ApprovingOrderPendings from './ApprovingOrderPendings';
import RejectedOrderPendings from './RejectedOrderPendings';
import ContractOrderPendings from './ContractOrderPendings';
import DeliveringOrderPendings from './DeliveringOrderPendings';
import PrePaymentOrderPendings from './PrePaymentOrderPendings';
import FinalCostVerificationOrderPendings from './FinalCostVerificationOrderPendings';
import RecentOrderPendings from './RecentOrderPendings';

import { countPendingOrdersByKeyword } from './common/pendingOrderKeywordMatch';
import { PendingSearchProvider, usePendingSearch } from './common/PendingSearchContext';
import { useOrderPerformanceFeatures } from '../../../providers/OrderPerformanceFeaturesProvider';
import {
	buildVisibleDashboardOrderPendingTabs,
} from '../../../utils/dashboard/dashboardOrderPendingTabs';
import {
	clearPendingApplyingReturnSnapshot,
	readPendingApplyingReturnSnapshot,
} from '../../../utils/order/pendingApplyingReturnSnapshot';

const PENDING_TAB_PANE_BY_KEY = {
	approvalPending: ApprovingOrderPendings,
	Applying: ApplyingOrderPendings,
	Rejected: RejectedOrderPendings,
	prePaymentOrder: PrePaymentOrderPendings,
	ContractPending: ContractOrderPendings,
	deliveryPending: DeliveringOrderPendings,
	acceptPending: AcceptingOrderPendings,
	accountsReceivablePending: AccountsReceivableOrderPendings,
	accountsReceivablePendingFinance: AccountsReceivableOrderPendings,
	ProcessingOrderBuyer: () => <RecentOrderPendings scope='buyer' />,
	ProcessingOrderFinance: () => <RecentOrderPendings scope='finance' />,
	finalCostVerification: FinalCostVerificationOrderPendings,
};


const PENDING_TAB_FROM_URL = new Set([
	'Rejected',
	'Applying',
	'approvalPending',
	'prePaymentOrder',
	'ContractPending',
	'acceptPending',
	'accountsReceivablePending',
	'accountsReceivablePendingFinance',
	'finalCostVerification',
	'deliveryPending',
]);

const TabTitle = ({ title: _title, subTitle, icon, notification_number = 0, compact = false }) => {
	const countLabel = notification_number > 99 ? '99+' : notification_number;

	const countBadge =
		notification_number > 0 ? (
			<span className='top-courses-tab-title__count' aria-label={`${notification_number} 条`}>
				{countLabel}
			</span>
		) : null;

	if (compact) {
		return (
			<div className='top-courses-tab-title top-courses-tab-title--compact d-inline-flex align-items-center text-nowrap'>
				<FontAwesomeIcon icon={icon} className='top-courses-tab-title__icon flex-shrink-0' />
				<span className='top-courses-tab-title__label'>{subTitle}</span>
				{countBadge}
			</div>
		);
	}

	return (
		<Flex className='top-courses-tab-title top-courses-tab-title--desktop py-2 py-md-3 text-start cursor-pointer align-items-center justify-content-center'>
			<div className='top-courses-tab-title__icon-wrap'>
				<FontAwesomeIcon icon={icon} className='fs-7' />
			</div>
			{/* 暂隐英文上行，只留中文；图标/标题/数字聚拢 */}
			<h5 className='mb-0 lh-1 fs-9'>{subTitle}</h5>
			{countBadge}
		</Flex>
	);
};

const getPendingTabScrollGuideStorageKey = (userId) => (userId ? `hasSeenPendingTabScrollGuide_${userId}` : null);

const TopCoursesTabScrollTrack = ({ children, enableCoachmark = false }) => {
	const { userInfo } = useSelector((state) => state.auth);
	const scrollRef = useRef(null);
	const dragRef = useRef({ tracking: false, dragging: false, startX: 0, startScrollLeft: 0, pointerId: null });
	const [showEndHint, setShowEndHint] = useState(false);
	const [showStartHint, setShowStartHint] = useState(false);
	const [showCoachmark, setShowCoachmark] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const dismissCoachmark = useCallback(
		(persist = true) => {
			setShowCoachmark(false);
			if (!persist) return;
			const storageKey = getPendingTabScrollGuideStorageKey(userInfo?._id);
			if (storageKey) {
				localStorage.setItem(storageKey, 'true');
			}
		},
		[userInfo?._id],
	);

	const updateScrollHints = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;

		const { scrollLeft, scrollWidth, clientWidth } = el;
		const canScroll = scrollWidth > clientWidth + 2;
		setShowEndHint(canScroll && scrollLeft < scrollWidth - clientWidth - 2);
		setShowStartHint(canScroll && scrollLeft > 2);

		if (showCoachmark && scrollLeft > 8) {
			dismissCoachmark(true);
		}
	}, [dismissCoachmark, showCoachmark]);

	useEffect(() => {
		updateScrollHints();
		const el = scrollRef.current;
		if (!el) return undefined;

		el.addEventListener('scroll', updateScrollHints, { passive: true });
		let rafId = 0;
		const scheduleUpdate = () => {
			if (rafId) return;
			rafId = window.requestAnimationFrame(() => {
				rafId = 0;
				updateScrollHints();
			});
		};
		const resizeObserver = new ResizeObserver(scheduleUpdate);
		resizeObserver.observe(el);
		if (el.firstElementChild) {
			resizeObserver.observe(el.firstElementChild);
		}

		return () => {
			el.removeEventListener('scroll', updateScrollHints);
			if (rafId) window.cancelAnimationFrame(rafId);
			resizeObserver.disconnect();
		};
	}, [updateScrollHints, children]);

	/** 桌面：悬停 Tab 栏时，纵向滚轮转为横向滚动 */
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return undefined;

		const onWheel = (event) => {
			const canScroll = el.scrollWidth > el.clientWidth + 2;
			if (!canScroll) return;

			const mostlyVertical = Math.abs(event.deltaY) >= Math.abs(event.deltaX);
			const delta = mostlyVertical ? event.deltaY : event.deltaX;
			if (!delta) return;

			const atStart = el.scrollLeft <= 0 && delta < 0;
			const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 && delta > 0;
			if (atStart || atEnd) return;

			event.preventDefault();
			el.scrollLeft += delta;
		};

		el.addEventListener('wheel', onWheel, { passive: false });
		return () => el.removeEventListener('wheel', onWheel);
	}, [children]);

	useEffect(() => {
		if (!enableCoachmark || !showEndHint || !userInfo?._id) {
			setShowCoachmark(false);
			return undefined;
		}

		const storageKey = getPendingTabScrollGuideStorageKey(userInfo._id);
		if (!storageKey || localStorage.getItem(storageKey)) {
			return undefined;
		}

		const timer = setTimeout(() => setShowCoachmark(true), 700);
		return () => clearTimeout(timer);
	}, [enableCoachmark, showEndHint, userInfo?._id]);

	const resetDrag = useCallback(() => {
		dragRef.current = { tracking: false, dragging: false, startX: 0, startScrollLeft: 0, pointerId: null };
		setIsDragging(false);
	}, []);

	const onPointerDown = useCallback((event) => {
		if (event.pointerType === 'touch' || event.button !== 0) return;
		const el = scrollRef.current;
		if (!el || el.scrollWidth <= el.clientWidth + 2) return;

		// 仅记录起点；超过阈值后再进入拖拽，避免吞掉 Tab 点击
		dragRef.current = {
			tracking: true,
			dragging: false,
			startX: event.clientX,
			startScrollLeft: el.scrollLeft,
			pointerId: event.pointerId,
		};
	}, []);

	const onPointerMove = useCallback((event) => {
		const drag = dragRef.current;
		const el = scrollRef.current;
		if (!drag.tracking || !el) return;

		const dx = event.clientX - drag.startX;
		if (!drag.dragging) {
			if (Math.abs(dx) < 6) return;
			drag.dragging = true;
			el.setPointerCapture?.(drag.pointerId);
			setIsDragging(true);
		}

		el.scrollLeft = drag.startScrollLeft - dx;
	}, []);

	const endPointerDrag = useCallback(
		(event) => {
			const drag = dragRef.current;
			const el = scrollRef.current;
			if (!drag.tracking) return;

			const wasDragging = drag.dragging;
			if (el && wasDragging && drag.pointerId != null) {
				try {
					el.releasePointerCapture?.(drag.pointerId);
				} catch {
					/* already released */
				}
			}

			resetDrag();

			if (wasDragging && event?.type === 'pointerup') {
				const suppressClick = (clickEvent) => {
					clickEvent.preventDefault();
					clickEvent.stopPropagation();
					el?.removeEventListener('click', suppressClick, true);
				};
				el?.addEventListener('click', suppressClick, true);
				window.setTimeout(() => el?.removeEventListener('click', suppressClick, true), 0);
			}
		},
		[resetDrag],
	);

	const scrollByPage = useCallback((direction) => {
		const el = scrollRef.current;
		if (!el) return;
		const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
		const step = Math.max(160, Math.round(el.clientWidth * 0.7));
		const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + direction * step));
		el.scrollTo({ left: next, behavior: 'smooth' });
	}, []);

	return (
		<div
			className={classNames('top-courses-tab-scroll-wrap', {
				'top-courses-tab-scroll-wrap--end-hint': showEndHint && !showCoachmark,
				'top-courses-tab-scroll-wrap--start-hint': showStartHint,
				'top-courses-tab-scroll-wrap--coachmark': showCoachmark,
				'top-courses-tab-scroll-wrap--dragging': isDragging,
			})}
		>
			<div
				ref={scrollRef}
				className='top-courses-tab-scroll'
				title='横向滚动查看更多分类'
				aria-label='待办分类，可横向滚动查看更多'
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={endPointerDrag}
				onPointerCancel={endPointerDrag}
			>
				{children}
			</div>
			{showCoachmark && (
				<div className='top-courses-tab-scroll-coachmark' role='dialog' aria-labelledby='pending-tab-scroll-guide-title'>
					<p id='pending-tab-scroll-guide-title' className='top-courses-tab-scroll-coachmark__text mb-0'>
						左右滑动可查看更多待办分类
					</p>
					<Button type='button' size='sm' variant='light' className='top-courses-tab-scroll-coachmark__btn' onClick={() => dismissCoachmark(true)}>
						知道了
					</Button>
				</div>
			)}
			{showStartHint && (
				<button
					type='button'
					className='top-courses-tab-scroll-hint top-courses-tab-scroll-hint--start top-courses-tab-scroll-hint--btn'
					aria-label='向左查看更多待办分类'
					onClick={() => scrollByPage(-1)}
				>
					<FontAwesomeIcon icon='chevron-left' className='top-courses-tab-scroll-hint__icon' />
				</button>
			)}
			{showEndHint && !showCoachmark && (
				<button
					type='button'
					className='top-courses-tab-scroll-hint top-courses-tab-scroll-hint--end top-courses-tab-scroll-hint--btn'
					aria-label='向右查看更多待办分类'
					onClick={() => scrollByPage(1)}
				>
					<FontAwesomeIcon icon='chevron-right' className='top-courses-tab-scroll-hint__icon' />
				</button>
			)}
		</div>
	);
};

const TopCoursesTabScroll = ({ children, enableCoachmark = false }) => <TopCoursesTabScrollTrack enableCoachmark={enableCoachmark}>{children}</TopCoursesTabScrollTrack>;

const ProcessingOrderCard = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const pendingTabFromUrl = searchParams.get('pendingTab');
	const pendingOrderFromUrl = searchParams.get('pendingOrder');
	const [snapshot] = useState(() => readPendingApplyingReturnSnapshot());

	const restoredTab = useMemo(() => {
		if (pendingTabFromUrl && PENDING_TAB_FROM_URL.has(pendingTabFromUrl)) return pendingTabFromUrl;
		return snapshot?.tab ?? null;
	}, [pendingTabFromUrl, snapshot?.tab]);

	useEffect(() => {
		if (snapshot) clearPendingApplyingReturnSnapshot();
	}, [snapshot]);

	useEffect(() => {
		if (!pendingTabFromUrl && !pendingOrderFromUrl) return;
		const next = new URLSearchParams(searchParams);
		next.delete('pendingTab');
		next.delete('pendingOrder');
		setSearchParams(next, { replace: true });
	}, [pendingOrderFromUrl, pendingTabFromUrl, searchParams, setSearchParams]);

	return (
		<PendingSearchProvider
			initialKeyword={pendingOrderFromUrl || snapshot?.keyword || ''}
			initialApplyingPageIndex={pendingOrderFromUrl ? null : snapshot?.pageIndex ?? null}
		>
			<ProcessingOrderCardContent restoredTab={restoredTab} />
		</PendingSearchProvider>
	);
};

const ProcessingOrderCardContent = ({ restoredTab = null }) => {
	const { userInfo } = useSelector((state) => state.auth);
	const { breakpoints } = useBreakpoints();
	const compactTabs = breakpoints.down('md');
	const orderPerformanceFeatures = useOrderPerformanceFeatures();
	const { keyword } = usePendingSearch();
	const [activeKey, setActiveKey] = useState(restoredTab);

	const tabTitleProps = useMemo(() => ({ compact: compactTabs }), [compactTabs]);

	const tabCount = useCallback(
		(orders) => {
			const list = Array.isArray(orders) ? orders : [];
			return String(keyword).trim() ? countPendingOrdersByKeyword(list, keyword) : list.length;
		},
		[keyword],
	);
	const {
		processingPendingBuyerOrders = [],
		processingPendingFinanceOrders = [],
		myApprovals = [],
		prePaymentOrders = [],
		finalCostVerificationOrders = [],
		contractOrders = [],
		isLoadingContractOrders,
		acceptingOrders = [],
		isLoadingAcceptingOrder,
		arOrders = [],
		isLoadingAROrders,
		deliveringOrders = [],
		isLoadingDeliveringOrder,
		applyingOrders = [],
		rejectedPendingOrders = [],
		isLoadingRejectedPendingOrders,
		isRejectedPendingOrdersUninitialized,
		refetchMyApprovals,
		refetchPrePaymentOrders,
		refetchFinalCostVerificationOrders,
		refetchContractOrders,
		refetchAcceptingOrders,
		refetchAROrders,
		refetchDeliveringOrders,
		refetchApplyingOrders,
		refetchMyRejectedOrders,
		refetchProcessingPending,
		shouldFetchApprovals,
		shouldFetchDelivering,
		shouldFetchAccepting,
		shouldFetchAR,
		shouldFetchApplying,
		shouldFetchRejected,
		shouldFetchPrePayment,
		shouldFetchFinalCostVerification,
		shouldFetchContract,
		shouldFetchProcessingPending,
		enablePendingLoad,
		expandAllPendingLoads,
	} = useOrderContext();

	/** 首 Tab 本帧先订阅；首屏 KPI/图表 batch 略让路后再并行补其余 Tab */
	useEffect(() => {
		let cancelled = false;
		const expand = () => {
			if (!cancelled) expandAllPendingLoads();
		};
		let raf1 = 0;
		let raf2 = 0;
		let timeoutId = 0;
		const scheduleExpand = () => {
			timeoutId = window.setTimeout(expand, 280);
		};
		if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
			raf1 = window.requestAnimationFrame(() => {
				raf2 = window.requestAnimationFrame(scheduleExpand);
			});
		} else {
			scheduleExpand();
		}
		return () => {
			cancelled = true;
			if (raf1) window.cancelAnimationFrame(raf1);
			if (raf2) window.cancelAnimationFrame(raf2);
			if (timeoutId) window.clearTimeout(timeoutId);
		};
	}, [expandAllPendingLoads]);

	const handleTabSelect = useCallback(
		(eventKey) => {
			setActiveKey(eventKey);
			const fetchKey = PENDING_TAB_FETCH_KEY[eventKey];
			if (fetchKey) enablePendingLoad(fetchKey);
		},
		[enablePendingLoad],
	);

	useEffect(() => {
		if (!restoredTab) return undefined;
		const fetchKey = PENDING_TAB_FETCH_KEY[restoredTab];
		if (fetchKey) enablePendingLoad(fetchKey);
		const refetchByTab = {
			Applying: refetchApplyingOrders,
			Rejected: refetchMyRejectedOrders,
			approvalPending: refetchMyApprovals,
			prePaymentOrder: refetchPrePaymentOrders,
			ContractPending: refetchContractOrders,
			deliveryPending: refetchDeliveringOrders,
			acceptPending: refetchAcceptingOrders,
			accountsReceivablePending: refetchAROrders,
			accountsReceivablePendingFinance: refetchAROrders,
			finalCostVerification: refetchFinalCostVerificationOrders,
		};
		const timer = window.setTimeout(() => {
			try {
				refetchByTab[restoredTab]?.();
			} catch {
				// 查询尚未订阅时忽略
			}
		}, 80);
		return () => window.clearTimeout(timer);
	}, [
		enablePendingLoad,
		refetchAROrders,
		refetchAcceptingOrders,
		refetchApplyingOrders,
		refetchContractOrders,
		refetchDeliveringOrders,
		refetchFinalCostVerificationOrders,
		refetchMyApprovals,
		refetchMyRejectedOrders,
		refetchPrePaymentOrders,
		restoredTab,
	]);

	const refetchOrders = useCallback(() => {
		if (!userInfo?.role) return;

		const safeRefetch = (refetch) => {
			if (!refetch) return;
			try {
				refetch();
			} catch {
				// 查询尚未订阅时忽略（skip 切换后的首帧等）
			}
		};

		const refetchConfig = [
			{ condition: shouldFetchPrePayment, refetch: refetchPrePaymentOrders, priority: true },
			{ condition: shouldFetchFinalCostVerification, refetch: refetchFinalCostVerificationOrders, priority: true },
			{ condition: shouldFetchApplying, refetch: refetchApplyingOrders, priority: true },
			{ condition: shouldFetchRejected, refetch: refetchMyRejectedOrders, priority: true },
			{ condition: shouldFetchApprovals, refetch: refetchMyApprovals, priority: true },
			{ condition: shouldFetchDelivering && !isLoadingDeliveringOrder, refetch: refetchDeliveringOrders },
			{ condition: shouldFetchAccepting && !isLoadingAcceptingOrder, refetch: refetchAcceptingOrders },
			{ condition: shouldFetchAR && !isLoadingAROrders, refetch: refetchAROrders },
			{ condition: shouldFetchContract && !isLoadingContractOrders, refetch: refetchContractOrders },
			{ condition: shouldFetchProcessingPending, refetch: refetchProcessingPending, priority: true },
		];

		refetchConfig.filter(({ condition, priority }) => priority && condition).forEach(({ refetch }) => safeRefetch(refetch));
		refetchConfig.filter(({ condition, priority }) => !priority && condition).forEach(({ refetch }) => safeRefetch(refetch));
	}, [
		isLoadingAROrders,
		isLoadingAcceptingOrder,
		isLoadingContractOrders,
		isLoadingDeliveringOrder,
		refetchAROrders,
		refetchAcceptingOrders,
		refetchApplyingOrders,
		refetchMyRejectedOrders,
		refetchContractOrders,
		refetchDeliveringOrders,
		refetchMyApprovals,
		refetchPrePaymentOrders,
		refetchFinalCostVerificationOrders,
		refetchProcessingPending,
		shouldFetchFinalCostVerification,
		shouldFetchAR,
		shouldFetchAccepting,
		shouldFetchApplying,
		shouldFetchRejected,
		shouldFetchApprovals,
		shouldFetchContract,
		shouldFetchDelivering,
		shouldFetchProcessingPending,
		shouldFetchPrePayment,
		userInfo?.role,
	]);

	useEffect(() => {
		const handleRefresh = (payload) => {
			if (payload?.scope === 'finalCostVerification') {
				if (shouldFetchFinalCostVerification) {
					try {
						refetchFinalCostVerificationOrders();
					} catch {
						/* 查询尚未订阅时忽略 */
					}
				}
				return;
			}
			refetchOrders();
		};

		socket.on('refresh processing orderlist', handleRefresh);

		return () => {
			socket.off('refresh processing orderlist', handleRefresh);
		};
	}, [refetchFinalCostVerificationOrders, refetchOrders, shouldFetchFinalCostVerification]);
	const rejectedPendingReady = !isRejectedPendingOrdersUninitialized && !isLoadingRejectedPendingOrders;
	const showRejectedTab = !rejectedPendingReady || rejectedPendingOrders.length > 0 || activeKey === 'Rejected';
	const rejectedTabCount = rejectedPendingReady ? tabCount(rejectedPendingOrders) : 0;

	const visibleTabs = useMemo(() => {
		const tabs = buildVisibleDashboardOrderPendingTabs({
			userInfo,
			orderPerformanceFeaturesEnabled: orderPerformanceFeatures.module,
			counts: {
				myApprovals: tabCount(myApprovals),
				applyingOrders: tabCount(applyingOrders),
				prePaymentOrders: tabCount(prePaymentOrders),
				contractOrders: tabCount(contractOrders),
				deliveringOrders: tabCount(deliveringOrders),
				acceptingOrders: tabCount(acceptingOrders),
				arOrders: tabCount(arOrders),
				buyerProcessingOrders: tabCount(processingPendingBuyerOrders),
				financeProcessingOrders: tabCount(processingPendingFinanceOrders),
				finalCostVerificationOrders: tabCount(finalCostVerificationOrders),
				rejectedOrders: rejectedTabCount,
			},
		});

		const rejected = tabs.find((t) => t.key === 'Rejected');
		if (rejected) {
			if (!showRejectedTab) {
				return tabs.filter((t) => t.key !== 'Rejected');
			}
			rejected.count = rejectedTabCount;
		}

		return tabs;
	}, [
		userInfo,
		orderPerformanceFeatures.module,
		showRejectedTab,
		rejectedTabCount,
		tabCount,
		myApprovals,
		applyingOrders,
		prePaymentOrders,
		contractOrders,
		deliveringOrders,
		acceptingOrders,
		arOrders,
		processingPendingBuyerOrders,
		processingPendingFinanceOrders,
		finalCostVerificationOrders,
	]);

	useEffect(() => {
		if (activeKey !== 'Rejected') return;
		if (isRejectedPendingOrdersUninitialized || isLoadingRejectedPendingOrders) return;
		if (rejectedPendingOrders.length > 0) return;
		setActiveKey(null);
	}, [activeKey, isLoadingRejectedPendingOrders, isRejectedPendingOrdersUninitialized, rejectedPendingOrders.length]);

	const renderTabContainer = useCallback(
		({ defaultActiveKey, tabs, tabPanes }) => (
			<Tab.Container id='top-course-tab' activeKey={activeKey || defaultActiveKey} onSelect={handleTabSelect}>
				<Card.Header className='p-0 overflow-hidden'>
					<TopCoursesTabScroll enableCoachmark={compactTabs}>
						<Nav
							className={classNames('nav-tabs top-courses-tab top-courses-tab--scroll border-0 flex-nowrap', {
								'top-courses-tab--compact': compactTabs,
							})}
						>
							{tabs}
						</Nav>
					</TopCoursesTabScroll>
				</Card.Header>
				<Card.Body className='p-0'>
					<Tab.Content>{tabPanes}</Tab.Content>
				</Card.Body>
			</Tab.Container>
		),
		[activeKey, compactTabs, handleTabSelect],
	);

	if (!userInfo || visibleTabs.length === 0) {
		return null;
	}

	return (
		<Card className='processing-order-card'>
			{renderTabContainer({
				defaultActiveKey: visibleTabs[0].key,
				tabs: visibleTabs.map((tab) => (
					<Nav.Item key={tab.key}>
						<Nav.Link className='mb-0' eventKey={tab.key}>
							<TabTitle {...tabTitleProps} title={tab.title} subTitle={tab.subTitle} icon={tab.icon} notification_number={tab.count} />
						</Nav.Link>
					</Nav.Item>
				)),
				tabPanes: visibleTabs.map((tab) => {
					const Pane = PENDING_TAB_PANE_BY_KEY[tab.key];
					if (!Pane) return null;
					return (
						<Tab.Pane unmountOnExit eventKey={tab.key} key={tab.key}>
							<Pane />
						</Tab.Pane>
					);
				}),
			})}
		</Card>
	);
};

export default ProcessingOrderCard;
